import React, { useState, useEffect, useCallback, useRef } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Popper from "@mui/material/Popper";
import { useFormContext } from "../contexts/FormContext";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface MainSkillEntry {
  name: string;
  functional_skills: string[];
}

interface TechnicalSkillEntry {
  domaine: string;
  competences: { competence: string; niveau: string }[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function splitToTags(raw: string): string[] {
  return raw
    .split(/[,\n]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function tagsToString(tags: string[]): string {
  return tags.join(", ");
}

// ─────────────────────────────────────────────────────────────────────────────
// MainSkillRow
// Fix : le state local rawText est géré indépendamment pour éviter la boucle
// de re-render qui causait la perte de focus.
// ─────────────────────────────────────────────────────────────────────────────

interface MainSkillRowProps {
  entry: MainSkillEntry;
  index: number;
  onUpdate: (index: number, updated: MainSkillEntry) => void;
  onRemove: (index: number) => void;
  showRemove: boolean;
}

const MainSkillRow: React.FC<MainSkillRowProps> = ({
  entry,
  index,
  onUpdate,
  onRemove,
  showRemove,
}) => {
  // ── Autocomplete pour le nom de la compétence principale ─────────────────
  const [nameInput, setNameInput] = useState(entry.name);
  const [nameOptions, setNameOptions] = useState<string[]>([]);
  const [nameLoading, setNameLoading] = useState(false);
  const [nameOpen, setNameOpen] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL;

  // Resync nameInput si le parent change (ex: chargement async)
  const prevNameRef = useRef(entry.name);
  useEffect(() => {
    if (prevNameRef.current !== entry.name && entry.name !== nameInput) {
      prevNameRef.current = entry.name;
      setNameInput(entry.name);
    }
  }, [entry.name]);

  const fetchNameSuggestions = useCallback(
    (q: string) => {
      if (q.trim().length < 2) {
        setNameOptions([]);
        setNameOpen(false);
        return;
      }
      setNameLoading(true);
      fetch(`${API_URL}/skills/main-skills?q=${encodeURIComponent(q)}`)
        .then((r) => r.json())
        .then((data: string[]) => {
          setNameOptions(data ?? []);
          setNameOpen((data ?? []).length > 0);
          setNameLoading(false);
        })
        .catch(() => {
          setNameOptions([]);
          setNameOpen(false);
          setNameLoading(false);
        });
    },
    [API_URL]
  );

  // ── rawText pour le textarea des compétences fonctionnelles ──────────────
  // On initialise une seule fois depuis la prop, puis on gère en local.
  // On ne resynchronise PLUS depuis la prop pour éviter la boucle de re-render.
  const [rawText, setRawText] = useState(() => tagsToString(entry.functional_skills));
  const isFirstRender = useRef(true);

  // Resync uniquement si les functional_skills arrivent de l'extérieur
  // (chargement async du formData) — mais pas sur chaque update local.
  const prevFSRef = useRef(entry.functional_skills);
  useEffect(() => {
    // Ignore le premier render (déjà initialisé via useState)
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    // Ne resync que si la prop change ET que rawText ne correspond pas
    // (= changement externe, pas un écho de notre propre update)
    if (prevFSRef.current !== entry.functional_skills) {
      prevFSRef.current = entry.functional_skills;
      const expected = tagsToString(entry.functional_skills);
      // On ne réécrase pas si le contenu parsé est identique (évite les boucles)
      if (splitToTags(rawText).join(",") !== entry.functional_skills.join(",")) {
        setRawText(expected);
      }
    }
  }, [entry.functional_skills]);

  const handleRawChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setRawText(value);
    onUpdate(index, { ...entry, functional_skills: splitToTags(value) });
  };

  return (
    <div
      style={{
        border: "1px solid #e3e6ee",
        borderRadius: "12px",
        padding: "1.2rem",
        marginBottom: "1rem",
        background: "#f9fafc",
      }}
    >
      {/* Nom de la compétence principale avec autocomplete */}
      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.8rem" }}>
        <span style={{ fontSize: "1.1rem", cursor: "grab" }}>📋</span>
        <Autocomplete
          freeSolo
          options={nameOptions}
          loading={nameLoading}
          open={nameOpen}
          onOpen={() => nameOptions.length > 0 && setNameOpen(true)}
          onClose={() => setNameOpen(false)}
          inputValue={nameInput}
          onInputChange={(_, value) => {
            setNameInput(value);
            onUpdate(index, { ...entry, name: value });
            fetchNameSuggestions(value);
          }}
          onChange={(_, value) => {
            if (value && typeof value === "string") {
              setNameInput(value);
              onUpdate(index, { ...entry, name: value });
              setNameOpen(false);
            }
          }}
          PopperComponent={(props) => (
            <Popper {...props} style={{ ...props.style, zIndex: 1300 }} />
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Compétence principale (ex : Gestion de projet)"
              variant="outlined"
              size="small"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {nameLoading ? <CircularProgress color="inherit" size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              sx={{ "& .MuiInputBase-input": { fontWeight: 500 } }}
            />
          )}
          sx={{ flex: 1 }}
        />
        {showRemove && (
          <button
            type="button"
            onClick={() => onRemove(index)}
            style={{ border: "none", background: "none", color: "red", cursor: "pointer", fontSize: "1.2rem" }}
          >
            ✕
          </button>
        )}
      </div>

      {/* Compétences fonctionnelles */}
      <div style={{ paddingLeft: "2rem" }}>
        <label
          style={{
            fontSize: "0.9rem",
            color: "#555",
            fontWeight: 500,
            display: "block",
            marginBottom: "0.4rem",
          }}
        >
          Compétences fonctionnelles{" "}
          <span style={{ fontWeight: 400, color: "#999" }}>
            — séparées par des virgules ou des sauts de ligne
          </span>
        </label>

        <textarea
          value={rawText}
          onChange={handleRawChange}
          placeholder="Ex : vision, roadmap, backlog, user stories, priorisation par valeur"
          rows={3}
          style={{
            width: "100%",
            padding: "0.6rem 0.8rem",
            borderRadius: "8px",
            border: "1px solid #ccc",
            fontSize: "0.95rem",
            fontFamily: "inherit",
            resize: "vertical",
            boxSizing: "border-box",
            lineHeight: "1.5",
          }}
        />

        {/* Aperçu des tags */}
        {entry.functional_skills.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginTop: "0.5rem" }}>
            {entry.functional_skills.map((fs, fi) => (
              <span
                key={fi}
                style={{
                  background: "#e8ecf7",
                  borderRadius: "20px",
                  padding: "0.2rem 0.65rem",
                  fontSize: "0.85rem",
                  color: "#1E3C73",
                }}
              >
                {fs}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SkillsForm
// ─────────────────────────────────────────────────────────────────────────────

const SkillsForm: React.FC = () => {
  const { formData, updateFormData } = useFormContext();

  const [mainSkillEntries, setMainSkillEntries] = useState<MainSkillEntry[]>(
    formData.main_skill_entries?.length > 0
      ? formData.main_skill_entries
      : [{ name: "", functional_skills: [] }]
  );

  const [technicalSkills, setTechnicalSkills] = useState<TechnicalSkillEntry[]>(
    formData.technical_skills?.length > 0
      ? formData.technical_skills
      : [{ domaine: "", competences: [{ competence: "", niveau: "" }] }]
  );

  const [showTechnical, setShowTechnical] = useState<boolean>(
    formData.show_technical_skills !== false
  );

  const [techDomainOptions, setTechDomainOptions] = useState<Record<string, string[]>>({});
  const [techDomainLoading, setTechDomainLoading] = useState<Record<string, boolean>>({});
  const [techDomainOpen, setTechDomainOpen] = useState<Record<string, boolean>>({});
  const [techSkillOptions, setTechSkillOptions] = useState<Record<string, string[]>>({});
  const [techSkillLoading, setTechSkillLoading] = useState<Record<string, boolean>>({});
  const [techSkillOpen, setTechSkillOpen] = useState<Record<string, boolean>>({});

  const API_URL = process.env.REACT_APP_API_URL;

  // ── Sync FormContext ─────────────────────────────────────────────────────
  useEffect(() => {
    (updateFormData as any)("main_skill_entries", mainSkillEntries);
  }, [mainSkillEntries]);

  useEffect(() => {
    updateFormData("technical_skills", technicalSkills);
  }, [technicalSkills]);

  useEffect(() => {
    (updateFormData as any)("show_technical_skills", showTechnical);
  }, [showTechnical]);

  // ── Handlers compétences principales ────────────────────────────────────
  const handleMainReorder = (result: any) => {
    if (!result.destination) return;
    const items = [...mainSkillEntries];
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    setMainSkillEntries(items);
  };

  const updateMainSkillEntry = useCallback((index: number, updated: MainSkillEntry) => {
    setMainSkillEntries((prev) => {
      const items = [...prev];
      items[index] = updated;
      return items;
    });
  }, []);

  const removeMainSkillEntry = useCallback((index: number) => {
    setMainSkillEntries((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // ── Handlers compétences techniques ─────────────────────────────────────
  const handleTechReorder = (result: any) => {
    if (!result.destination) return;
    const items = [...technicalSkills];
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    setTechnicalSkills(items);
  };

  const fetchTechDomain = (idx: number, value: string) => {
    const key = `domain-${idx}`;
    if (!value.trim()) {
      setTechDomainOptions((p) => ({ ...p, [key]: [] }));
      setTechDomainOpen((p) => ({ ...p, [key]: false }));
      return;
    }
    setTechDomainLoading((p) => ({ ...p, [key]: true }));
    fetch(`${API_URL}/skills/technical-domains?q=${encodeURIComponent(value)}`)
      .then((r) => r.json())
      .then((data: string[]) => {
        setTechDomainOptions((p) => ({ ...p, [key]: data ?? [] }));
        setTechDomainOpen((p) => ({ ...p, [key]: (data ?? []).length > 0 }));
        setTechDomainLoading((p) => ({ ...p, [key]: false }));
      })
      .catch(() => {
        setTechDomainOptions((p) => ({ ...p, [key]: [] }));
        setTechDomainOpen((p) => ({ ...p, [key]: false }));
        setTechDomainLoading((p) => ({ ...p, [key]: false }));
      });
  };

  const fetchTechSkill = (key: string, value: string) => {
    if (value.trim().length < 2) {
      setTechSkillOptions((p) => ({ ...p, [key]: [] }));
      setTechSkillOpen((p) => ({ ...p, [key]: false }));
      return;
    }
    setTechSkillLoading((p) => ({ ...p, [key]: true }));
    fetch(`${API_URL}/skills/technical?q=${encodeURIComponent(value)}`)
      .then((r) => r.json())
      .then((data: string[]) => {
        setTechSkillOptions((p) => ({ ...p, [key]: data ?? [] }));
        setTechSkillOpen((p) => ({ ...p, [key]: (data ?? []).length > 0 }));
        setTechSkillLoading((p) => ({ ...p, [key]: false }));
      })
      .catch(() => {
        setTechSkillOptions((p) => ({ ...p, [key]: [] }));
        setTechSkillOpen((p) => ({ ...p, [key]: false }));
        setTechSkillLoading((p) => ({ ...p, [key]: false }));
      });
  };

  const addBtnStyle: React.CSSProperties = {
    padding: "0.4rem 1rem",
    borderRadius: "8px",
    border: "1px solid #ccc",
    background: "#eee",
    cursor: "pointer",
    fontSize: "1rem",
  };

  return (
    <div>
      {/* ── COMPÉTENCES PRINCIPALES + FONCTIONNELLES ──────────────────── */}
      <div style={{ marginBottom: "1.5rem" }}>
        <label style={{ display: "block", marginBottom: "0.3rem", fontWeight: 500, fontSize: "1.05rem" }}>
          Compétences principales &amp; fonctionnelles
        </label>
        <p style={{ fontSize: "0.9em", color: "#666", margin: "0 0 0.8rem 0" }}>
          Pour chaque compétence principale, listez les compétences fonctionnelles séparées par des
          virgules. Glissez-déposez pour réordonner.
        </p>

        <DragDropContext onDragEnd={handleMainReorder}>
          <Droppable droppableId="mainSkillEntries">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {mainSkillEntries.map((entry, idx) => (
                  <Draggable key={`mse-${idx}`} draggableId={`mse-${idx}`} index={idx}>
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={{ ...provided.draggableProps.style }}
                      >
                        <MainSkillRow
                          entry={entry}
                          index={idx}
                          onUpdate={updateMainSkillEntry}
                          onRemove={removeMainSkillEntry}
                          showRemove={mainSkillEntries.length > 1}
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        <div style={{ textAlign: "right" }}>
          <button
            type="button"
            onClick={() =>
              setMainSkillEntries([...mainSkillEntries, { name: "", functional_skills: [] }])
            }
            style={addBtnStyle}
          >
            + Compétence principale
          </button>
        </div>
      </div>

      {/* ── COMPÉTENCES TECHNIQUES ────────────────────────────────────── */}
      <div style={{ marginBottom: "1.2rem" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "0.5rem",
          }}
        >
          <label style={{ fontWeight: 500, fontSize: "1.05rem" }}>Compétences techniques</label>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              fontSize: "0.9rem",
              color: "#555",
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={showTechnical}
              onChange={(e) => setShowTechnical(e.target.checked)}
              style={{ width: "16px", height: "16px", cursor: "pointer" }}
            />
            Afficher dans le CV
          </label>
        </div>
        <p style={{ fontSize: "0.9em", color: "#666", margin: "0 0 0.8rem 0" }}>
          Ces données sont toujours enregistrées. L'option ci-dessus contrôle uniquement leur
          affichage dans le CV généré.
        </p>

        <DragDropContext onDragEnd={handleTechReorder}>
          <Droppable droppableId="technicalSkills">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {technicalSkills.map((skill, idx) => (
                  <Draggable key={`tech-${idx}`} draggableId={`tech-${idx}`} index={idx}>
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        style={{
                          ...provided.draggableProps.style,
                          border: "1px solid #e3e6ee",
                          borderRadius: "12px",
                          padding: "1.2rem",
                          marginBottom: "1.2rem",
                          background: "#f9fafc",
                        }}
                      >
                        <div
                          {...provided.dragHandleProps}
                          style={{ cursor: "grab", marginBottom: "0.5rem" }}
                        >
                          🔧
                        </div>

                        {/* Domaine */}
                        <Autocomplete
                          freeSolo
                          options={techDomainOptions[`domain-${idx}`] ?? []}
                          loading={techDomainLoading[`domain-${idx}`] ?? false}
                          open={techDomainOpen[`domain-${idx}`] ?? false}
                          onOpen={() => {
                            if ((techDomainOptions[`domain-${idx}`] ?? []).length > 0)
                              setTechDomainOpen((p) => ({ ...p, [`domain-${idx}`]: true }));
                          }}
                          onClose={() =>
                            setTechDomainOpen((p) => ({ ...p, [`domain-${idx}`]: false }))
                          }
                          inputValue={skill.domaine}
                          onInputChange={(_, value) => {
                            const items = [...technicalSkills];
                            items[idx].domaine = value;
                            setTechnicalSkills(items);
                            fetchTechDomain(idx, value);
                          }}
                          onChange={(_, value) => {
                            if (value && typeof value === "string") {
                              const items = [...technicalSkills];
                              items[idx].domaine = value;
                              setTechnicalSkills(items);
                              setTechDomainOpen((p) => ({ ...p, [`domain-${idx}`]: false }));
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Domaine technique (ex : Outils collaboratifs)"
                              variant="outlined"
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {techDomainLoading[`domain-${idx}`] ? (
                                      <CircularProgress color="inherit" size={20} />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                              sx={{ marginBottom: "0.7rem" }}
                            />
                          )}
                        />

                        {/* Compétences du domaine */}
                        <label style={{ fontWeight: 500, display: "block", marginBottom: "0.3rem" }}>
                          Compétences et niveaux :
                        </label>
                        {skill.competences.map((c, cIdx) => {
                          const key = `tech-${idx}-${cIdx}`;
                          return (
                            <div
                              key={cIdx}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "0.7rem",
                                marginBottom: "0.5rem",
                              }}
                            >
                              <Autocomplete
                                freeSolo
                                options={techSkillOptions[key] ?? []}
                                loading={techSkillLoading[key] ?? false}
                                open={techSkillOpen[key] ?? false}
                                onOpen={() => {
                                  if ((techSkillOptions[key] ?? []).length > 0)
                                    setTechSkillOpen((p) => ({ ...p, [key]: true }));
                                }}
                                onClose={() =>
                                  setTechSkillOpen((p) => ({ ...p, [key]: false }))
                                }
                                inputValue={c.competence}
                                onInputChange={(_, value) => {
                                  const items = [...technicalSkills];
                                  items[idx].competences[cIdx].competence = value;
                                  setTechnicalSkills(items);
                                  fetchTechSkill(key, value);
                                }}
                                onChange={(_, value) => {
                                  if (value && typeof value === "string") {
                                    const items = [...technicalSkills];
                                    items[idx].competences[cIdx].competence = value;
                                    setTechnicalSkills(items);
                                    setTechSkillOpen((p) => ({ ...p, [key]: false }));
                                  }
                                }}
                                noOptionsText="Aucune compétence trouvée"
                                ListboxProps={{ style: { maxHeight: "200px" } }}
                                PopperComponent={(props) => (
                                  <Popper {...props} style={{ ...props.style, zIndex: 1300 }} />
                                )}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    placeholder="Compétence (ex : JIRA)"
                                    variant="outlined"
                                    size="small"
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter" && c.competence.trim() && c.niveau) {
                                        e.preventDefault();
                                        const items = [...technicalSkills];
                                        items[idx].competences.push({ competence: "", niveau: "" });
                                        setTechnicalSkills(items);
                                      }
                                    }}
                                    InputProps={{
                                      ...params.InputProps,
                                      endAdornment: (
                                        <>
                                          {techSkillLoading[key] ? (
                                            <CircularProgress color="inherit" size={18} />
                                          ) : null}
                                          {params.InputProps.endAdornment}
                                        </>
                                      ),
                                    }}
                                    sx={{ minWidth: "200px" }}
                                  />
                                )}
                              />
                              <select
                                value={c.niveau}
                                onChange={(e) => {
                                  const items = [...technicalSkills];
                                  items[idx].competences[cIdx].niveau = e.target.value;
                                  setTechnicalSkills(items);
                                }}
                                style={{
                                  padding: "0.5rem",
                                  borderRadius: "6px",
                                  border: "1px solid #ccc",
                                  fontSize: "1rem",
                                  minWidth: "130px",
                                }}
                              >
                                <option value="">Niveau</option>
                                <option value="1">1 - Connaissance</option>
                                <option value="2">2 - Maîtrise</option>
                                <option value="3">3 - Expert</option>
                              </select>
                              {skill.competences.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    const items = [...technicalSkills];
                                    items[idx].competences = items[idx].competences.filter(
                                      (_, i) => i !== cIdx
                                    );
                                    setTechnicalSkills(items);
                                  }}
                                  style={{
                                    border: "none",
                                    background: "none",
                                    color: "red",
                                    cursor: "pointer",
                                    fontSize: "1.2rem",
                                  }}
                                >
                                  ✕
                                </button>
                              )}
                            </div>
                          );
                        })}

                        <button
                          type="button"
                          onClick={() => {
                            const items = [...technicalSkills];
                            items[idx].competences.push({ competence: "", niveau: "" });
                            setTechnicalSkills(items);
                          }}
                          style={{
                            padding: "0.3rem 0.7rem",
                            borderRadius: "6px",
                            border: "1px solid #ccc",
                            background: "#eee",
                            cursor: "pointer",
                            fontSize: "1rem",
                          }}
                        >
                          + Compétence
                        </button>

                        {technicalSkills.length > 1 && (
                          <div style={{ textAlign: "right", marginTop: "0.7rem" }}>
                            <button
                              type="button"
                              onClick={() =>
                                setTechnicalSkills(technicalSkills.filter((_, i) => i !== idx))
                              }
                              style={{
                                border: "none",
                                background: "none",
                                color: "red",
                                cursor: "pointer",
                                fontSize: "1.2rem",
                              }}
                            >
                              ✕ Supprimer ce domaine
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        <div style={{ textAlign: "right" }}>
          <button
            type="button"
            onClick={() =>
              setTechnicalSkills([
                ...technicalSkills,
                { domaine: "", competences: [{ competence: "", niveau: "" }] },
              ])
            }
            style={{
              padding: "0.3rem 0.7rem",
              borderRadius: "6px",
              border: "1px solid #ccc",
              background: "#eee",
              cursor: "pointer",
              fontSize: "1rem",
            }}
          >
            + Domaine technique
          </button>
        </div>
      </div>
    </div>
  );
};

export default SkillsForm;